// ProjectInfoForm.js — shared by New Project Intake and Edit Project.
//
// Box 1: jurisdiction, inspections (disciplines), firm, and an engineer per
// discipline. Box 2: Section 1 of the chosen jurisdiction's form, in form order
// and wording, plus the notice date (date or N/A).

import React from 'react';
import { useNavigate } from 'react-router-dom';
import DisciplineChecks from './DisciplineChecks';
import { DISCIPLINES } from '../disciplines';
import { JURISDICTIONS, section1Fields, referenceField, noticeItem, NA } from '../projectFields';

const ProjectInfoForm = ({ form, setForm, roster, signer, setSigner }) => {
  const navigate = useNavigate();
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const onInput = (e) => set(e.target.name, e.target.value);
  const fields = form.county ? section1Fields(form.county, form.disciplines) : [];
  const ref = form.county ? referenceField(form.county) : null;
  const noticeNA = form.noticeDate === NA;
  const rosterEmpty = !roster.engineers.length || !roster.firms.length;

  const input = (key, extra = {}) => (
    <input id={`pf-${key}`} name={key} value={form[key] ?? ''} onChange={onInput} style={styles.input} {...extra} />
  );

  const renderField = (f) => {
    const id = `pf-${f.key}`;
    const lbl = <label style={styles.label} htmlFor={id}>{f.item}. {f.label}</label>;

    if (f.type === 'address') {
      return (
        <div key={f.key} style={styles.full}>
          <span style={styles.label}>{f.item}. {f.label} *</span>
          <div style={styles.row}>
            <div style={{ ...styles.field, flex: 3 }}>{input('address', { placeholder: 'Street', required: true, 'aria-label': 'Street' })}</div>
            <div style={{ ...styles.field, flex: 2 }}>{input('city', { placeholder: 'City', required: true, 'aria-label': 'City' })}</div>
            <div style={{ ...styles.field, flex: '0 0 60px', minWidth: 60 }}>{input('state', { 'aria-label': 'State' })}</div>
            <div style={{ ...styles.field, flex: '0 0 90px', minWidth: 90 }}>{input('zip', { placeholder: 'ZIP', required: true, 'aria-label': 'ZIP' })}</div>
            {f.bldg && <div style={{ ...styles.field, flex: '0 0 90px', minWidth: 90 }}>{input('buildingNumber', { placeholder: 'Bldg. #', 'aria-label': 'Building number' })}</div>}
          </div>
        </div>
      );
    }
    if (f.type === 'title') {
      return (
        <div key={f.key} style={styles.full}>
          {lbl}
          {input('nameOnTitle', { placeholder: 'e.g. Ocean Tower Condominium Association, Inc.' })}
          <label style={styles.check}>
            <input type="checkbox" checked={!!form.ownerDiffers} onChange={e => set('ownerDiffers', e.target.checked)} />
            Owner’s name differs from the name on title
          </label>
        </div>
      );
    }
    if (f.type === 'owner') {
      if (!form.ownerDiffers) return null;
      return <div key={f.key} style={styles.full}>{lbl}{input('ownerName')}</div>;
    }
    if (f.type === 'yesno') {
      return (
        <div key={f.key} style={styles.full}>
          <span style={styles.label}>{f.item}. {f.label}</span>
          <div style={styles.btnRow}>
            {['Yes', 'No'].map(v => (
              <button key={v} type="button" aria-pressed={form[f.key] === v}
                style={{ ...styles.choice, ...(form[f.key] === v ? styles.choiceOn : {}) }}
                onClick={() => set(f.key, v)}>{v}</button>
            ))}
          </div>
        </div>
      );
    }
    if (f.type === 'area') {
      return (
        <div key={f.key} style={styles.full}>
          {lbl}
          <textarea id={id} name={f.key} rows={2} value={form[f.key] ?? ''} onChange={onInput} style={{ ...styles.input, resize: 'vertical' }} />
        </div>
      );
    }
    return (
      <div key={f.key} style={f.short ? styles.short : styles.field}>
        {lbl}
        {input(f.key)}
      </div>
    );
  };

  return (
    <>
      {/* ── Box 1 ── */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Jurisdiction, inspections and engineer</h3>
        <div style={styles.row}>
          <div style={{ ...styles.field, maxWidth: 300 }}>
            <label style={styles.label} htmlFor="pf-county">Jurisdiction *</label>
            <select id="pf-county" name="county" value={form.county} onChange={onInput} style={styles.input} required>
              <option value="">— Select jurisdiction —</option>
              {JURISDICTIONS.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
            </select>
          </div>
          <div style={styles.field}>
            <span style={styles.label}>Inspections *</span>
            <DisciplineChecks value={form.disciplines} onChange={v => set('disciplines', v)} />
          </div>
        </div>

        {rosterEmpty && (
          <p style={styles.hint}>
            Add engineers and firms in Settings first.{' '}
            <span style={styles.link} onClick={() => navigate('/settings')}>Open Settings</span>
          </p>
        )}
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="pf-firm">Firm</label>
            <select id="pf-firm" value={signer.firmId || ''} onChange={e => setSigner(s => ({ ...s, firmId: e.target.value }))} style={styles.input}>
              <option value="">— Select firm —</option>
              {roster.firms.map(f => <option key={f.id} value={f.id}>{f.name || '(unnamed)'}</option>)}
            </select>
          </div>
          {DISCIPLINES.filter(d => form.disciplines.includes(d.key)).map(d => (
            <div key={d.key} style={styles.field}>
              <label style={styles.label} htmlFor={`pf-eng-${d.key}`}>{d.label} engineer</label>
              <select id={`pf-eng-${d.key}`} value={signer[d.key] || ''} onChange={e => setSigner(s => ({ ...s, [d.key]: e.target.value }))} style={styles.input}>
                <option value="">— Select engineer —</option>
                {roster.engineers.map(en => <option key={en.id} value={en.id}>{en.name || '(unnamed)'}{en.discipline ? ` — ${en.discipline}` : ''}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* ── Box 2: Section 1 ── */}
      {form.county ? (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>1. Description of building</h3>
          {ref && (
            <div style={styles.row}>
              <div style={{ ...styles.field, maxWidth: 300 }}>
                <label style={styles.label} htmlFor={`pf-${ref.key}`}>{ref.label}</label>
                {input(ref.key)}
              </div>
            </div>
          )}
          <div style={styles.row}>{fields.map(renderField)}</div>

          <div style={{ ...styles.row, marginTop: '0.5rem' }}>
            <div style={{ ...styles.field, maxWidth: 360 }}>
              <label style={styles.label} htmlFor="pf-notice">{noticeItem(form.county)}. Date of Notice of Required Inspection</label>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input id="pf-notice" type="date" disabled={noticeNA} value={noticeNA ? '' : form.noticeDate || ''}
                  onChange={e => set('noticeDate', e.target.value)} style={{ ...styles.input, maxWidth: 200, opacity: noticeNA ? 0.5 : 1 }} />
                <label style={{ ...styles.check, marginTop: 0 }}>
                  <input type="checkbox" checked={noticeNA} onChange={e => set('noticeDate', e.target.checked ? NA : '')} />
                  N/A
                </label>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p style={styles.hint}>Select the jurisdiction to show its Section 1 fields.</p>
      )}
    </>
  );
};

const styles = {
  section:      { marginBottom: '1.75rem', paddingBottom: '1.25rem', borderBottom: '1px solid #e5e7eb' },
  sectionTitle: { margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' },
  row:          { display: 'flex', gap: '0.75rem 1rem', flexWrap: 'wrap', alignItems: 'flex-end' },
  field:        { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '220px', marginBottom: '0.75rem' },
  short:        { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '0 1 200px', minWidth: '160px', marginBottom: '0.75rem' },
  full:         { display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: '1 1 100%', marginBottom: '0.75rem' },
  label:        { fontSize: '0.875rem', fontWeight: '500', color: '#374151' },
  input:        { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  check:        { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: '#374151', marginTop: '0.35rem', cursor: 'pointer' },
  btnRow:       { display: 'flex', gap: '0.5rem' },
  choice:       { padding: '0.4rem 1.25rem', border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f3f4f6', fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'inherit' },
  choiceOn:     { backgroundColor: '#112b49', borderColor: '#112b49', color: 'white', fontWeight: 600 },
  hint:         { margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#4b5563' },
  link:         { color: 'cornflowerblue', cursor: 'pointer', fontWeight: '600', textDecoration: 'underline' },
};

export default ProjectInfoForm;
